import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Cupo } from '@eventos-cien/shared';
import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { TextField } from '../components/TextField';
import { apiAuth, ApiError } from '../lib/apiAuth';
import { useAdminEventoData } from './AdminEventoDataProvider';

interface RespLote {
  creadas: number;
  aforo: { objetivo: number; comprometido: number; disponible: number };
  advertencia: string | null;
}
interface RespRetiro {
  retiradas: number;
  aforo: { disponible: number };
}

export function LotesScreen() {
  const { eventoId, usuarios, miembros, invitaciones } = useAdminEventoData();

  const asesoresDelEvento = useMemo(
    () =>
      miembros
        .filter((m) => m.activo && m.roles.includes('asesor'))
        .map((m) => usuarios.find((u) => u.uid === m.uid))
        .filter((u): u is NonNullable<typeof u> => Boolean(u)),
    [miembros, usuarios],
  );

  const porAsesor = useMemo(() => {
    const m = new Map<string, { disp: number; total: number }>();
    for (const i of invitaciones) {
      const e = m.get(i.asesorUid) ?? { disp: 0, total: 0 };
      e.total += 1;
      if (i.estado === 'disponible') e.disp += 1;
      m.set(i.asesorUid, e);
    }
    return m;
  }, [invitaciones]);

  const [asesorUid, setAsesorUid] = useState('');
  const [cupo, setCupo] = useState<Cupo>(1);
  const [cantidad, setCantidad] = useState('50');
  const [modo, setModo] = useState<'crear' | 'retirar'>('crear');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ tono: 'exito' | 'alerta' | 'error'; texto: string } | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (!asesorUid) {
      setMsg({ tono: 'error', texto: 'Elige un asesor' });
      return;
    }
    setEnviando(true);
    try {
      const cant = Number(cantidad);
      if (modo === 'crear') {
        const r = await apiAuth<RespLote>(`/admin/eventos/${eventoId}/lote`, {
          method: 'POST',
          body: JSON.stringify({ asesorUid, cupo, cantidad: cant }),
        });
        setMsg({
          tono: r.advertencia ? 'alerta' : 'exito',
          texto: `${r.creadas} invitaciones creadas. ` + (r.advertencia ?? `Disponible: ${r.aforo.disponible}.`),
        });
      } else {
        const r = await apiAuth<RespRetiro>(`/admin/eventos/${eventoId}/lote`, {
          method: 'DELETE',
          body: JSON.stringify({ asesorUid, cantidad: cant }),
        });
        setMsg({
          tono: 'exito',
          texto: `${r.retiradas} invitaciones retiradas. Disponible: ${r.aforo.disponible}.`,
        });
      }
    } catch (err) {
      setMsg({ tono: 'error', texto: err instanceof ApiError ? err.message : 'No se pudo completar' });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="max-w-xl space-y-6">
      <Card className="p-5">
        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          <h2 className="text-sm font-semibold text-texto">Lotes de invitación</h2>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-texto" htmlFor="sel-asesor">
              Asesor
            </label>
            <select
              id="sel-asesor"
              className="w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
              value={asesorUid}
              onChange={(e) => setAsesorUid(e.target.value)}
            >
              <option value="">Elige…</option>
              {asesoresDelEvento.map((a) => {
                const c = porAsesor.get(a.uid);
                return (
                  <option key={a.uid} value={a.uid}>
                    {a.nombre} ({c?.disp ?? 0} por enviar / {c?.total ?? 0})
                  </option>
                );
              })}
            </select>
            {asesoresDelEvento.length === 0 && (
              <p className="text-xs text-texto/50">
                Todavía no hay asesores vinculados a este evento —{' '}
                <Link to="../equipo" className="font-medium text-navy underline">
                  vincula uno en Equipo
                </Link>
                .
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-texto">Modo</span>
            <div className="flex gap-2">
              <Chip activo={modo === 'crear'} onClick={() => setModo('crear')}>
                Crear
              </Chip>
              <Chip activo={modo === 'retirar'} onClick={() => setModo('retirar')}>
                Retirar
              </Chip>
            </div>
          </div>

          {modo === 'crear' && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-texto">Cupo</span>
              <div className="flex gap-2">
                <Chip activo={cupo === 1} onClick={() => setCupo(1)}>
                  Individual
                </Chip>
                <Chip activo={cupo === 2} onClick={() => setCupo(2)}>
                  Pareja
                </Chip>
              </div>
            </div>
          )}

          <TextField
            label="Cantidad"
            type="number"
            inputMode="numeric"
            min={1}
            max={500}
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
          />

          {msg && <Callout tono={msg.tono}>{msg.texto}</Callout>}

          <Button type="submit" cargando={enviando}>
            {modo === 'crear' ? 'Crear lote' : 'Retirar del lote'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
